import types from './types';

import CategoryModel from 'src/model/category.model';
import SubcategoryModel from 'src/model/subcategory.model';
import ProductModel from 'src/model/product.model';

import CategoryService from 'src/services/categories/CategoriesService';
import SubcategoryService from 'src/services/subcategories/subcategoryService';

import { paginationActions } from 'src/store/modules/pagination';
import SubcategoryFilter from 'src/model/subcategoryFilter.model';
import SubcategoryBrandFilter from 'src/model/subcategoryBrandFilter.model';
import SubcategoryAttributeFilter from 'src/model/subcategoryAttributeFilter.model';

import { analyticsActions } from 'src/analytics/store';

const productsPerPage = 8;

/* Data getting */

/* *** GetCategories: Retrieves categories list from API to display in categories dropdown */
const getCategories = () => {
  return (dispatch: any) => {
    dispatch(getCategoryPending(true));
    CategoryService.getCategories()
      .then((categories: CategoryModel[]) => {
        dispatch(setCategories(categories));
      })
      .catch(error => {
        dispatch(categoriesError(true, error));
      });
  };
};

const getSubcategories = (id: string) => {
  return (dispatch: any) => {
    dispatch(getSubcategoriesPending(true));
    SubcategoryService.getSubcategories(id)
      .then((response: SubcategoryModel[]) => {
        dispatch(setSubcategories(response));
        dispatch(getSubcategoriesPending(false));
      })
      .catch(error => {
        console.warn(error);
        dispatch(categoriesError(true, error));
        dispatch(getSubcategoriesPending(false));
      });
  };
};

const getProductsByCategory = (
  id: string,
  categoryName: string,
  customerId: string,
  pageNumber: number = 0,
  pageSize: number = 12
) => {
  return (dispatch: any) => {
    dispatch(getProductsByCategoryPending(true));
    // Clear old products
    dispatch(setProducts([]));
    SubcategoryService.getProductsBySubcategory(
      id,
      customerId,
      pageNumber,
      pageSize
    )
      .then((resolve: any) => {
        dispatch(setProducts(resolve.data));
        dispatch(setActiveSubcategory({ id, name: categoryName }));
        dispatch(paginationActions.setTotalPages(resolve.totalPages));
        dispatch(paginationActions.setPageSize(resolve.pageSize));
        dispatch(paginationActions.setTotalElements(resolve.totalElements));
        dispatch(paginationActions.setPageNumber(resolve.pageNumber));
        dispatch(getProductsByCategoryPending(false));
        dispatch(analyticsActions.searchByCategory());
      })
      .catch(error => {
        dispatch(categoriesError(true, error));
        dispatch(getProductsByCategoryPending(false));
      });
  };
};

const getFilteredProducts = (
  id: string,
  filters: SubcategoryFilter[],
  selectionType: string,
  pageNumber: number = 0,
  pageSize: number = 12
) => {
  return (dispatch: any) => {
    dispatch(getFilteredProductsPending(true));
    dispatch(getProductsByCategoryPending(true));

    SubcategoryService.getSubcategoriesFiltered(
      id,
      filters,
      selectionType,
      pageNumber,
      pageSize
    )
      .then((resolve: any) => {
        dispatch(setFilteredProducts(resolve.data));
        dispatch(paginationActions.setTotalPages(resolve.totalPages));
        dispatch(paginationActions.setPageSize(resolve.pageSize));
        dispatch(paginationActions.setTotalElements(resolve.totalElements));
        dispatch(paginationActions.setPageNumber(resolve.pageNumber));
        dispatch(getFilteredProductsPending(false));
        dispatch(getProductsByCategoryPending(false));
      })
      .catch(error => {
        dispatch(categoriesError(true, error));
        dispatch(getFilteredProductsPending(false));
        dispatch(getProductsByCategoryPending(false));
      });
  };
};

const getFiltersBySubcategory = (subcategoryId: string) => {
  return (dispatch: any) => {
    const empty: any = [];
    dispatch(setFilters(empty));
    dispatch(getFiltersBySubcategoryPending(true));
    SubcategoryService.getSubcategoriesFilters(subcategoryId)
      .then((resolve: any) => {
        const filters: SubcategoryFilter[] = resolve.data;
        dispatch(setFilters(filters));
        dispatch(getFiltersBySubcategoryPending(false));
      })
      .catch(error => {
        dispatch(categoriesError(true, error));
        dispatch(getFiltersBySubcategoryPending(false));
      });
  };
};

const getBrandsBySubcategory = (subcategoryId: string) => {
  return (dispatch: any) => {
    const empty: any = []; 
    dispatch(setBrands(empty));
    dispatch(getBrandsBySubcategoryPending(true));
    SubcategoryService.getSubcategoriesBrands(subcategoryId)
      .then((resolve: any) => {
        const brands: SubcategoryBrandFilter[] = resolve.data;
        
        dispatch(setBrands(brands));
        dispatch(getBrandsBySubcategoryPending(false));
      })
      .catch(error => {
        dispatch(categoriesError(true, error));
        dispatch(getBrandsBySubcategoryPending(false));
      });
  };
};

const getAttributesBySubcategory = (subcategoryId: string) => {
  return (dispatch: any) => {
    const empty: any = []; 
    dispatch(setAttributes(empty));
    dispatch(getAttributesBySubcategoryPending(true));
    SubcategoryService.getSubcategoriesAttributes(subcategoryId)
      .then((resolve: any) => {
        const attributes: SubcategoryAttributeFilter[] = resolve.data;
        
        console.log("attribute fetch Data",attributes);
        dispatch(setAttributes(attributes));
        dispatch(getAttributesBySubcategoryPending(false));
      })
      .catch(error => {
        dispatch(categoriesError(true, error));
        dispatch(getAttributesBySubcategoryPending(false));
      });
  };
};

/* Data pending */

const getCategoryPending = (isPending: boolean) => {
  return { type: types.CATEGORIES_PENDING, payload: isPending };
};

const getSubcategoriesPending = (isPending: boolean) => {
  return { type: types.SUBCATEGORIES_PENDING, payload: isPending };
};

const getProductsByCategoryPending = (isPending: boolean) => {
  return { type: types.PRODUCTS_PENDING, payload: isPending };
};

const getFiltersBySubcategoryPending = (isPending: boolean) => {
  return { type: types.SUBCATEGORIES_FILTERS_PENDING, payload: isPending };
};

const getBrandsBySubcategoryPending = (isPending: boolean) => {
  return { type: types.SUBCATEGORIES_BRANDS_PENDING, payload: isPending };
};

const getAttributesBySubcategoryPending = (isPending: boolean) => {
  return { type: types.SUBCATEGORIES_ATTRIBUTES_PENDING, payload: isPending };
};

const getFilteredProductsPending = (isPending: boolean) => {
  return { type: types.FILTERED_PRODUCTS_PENDING, payload: isPending };
};

/* Data setting */

const setCategories = (categories: CategoryModel[]) => {
  return { type: types.SET_CATEGORIES, payload: categories };
};

const setSubcategories = (subcategories: SubcategoryModel[]) => {
  return { type: types.SET_SUBCATEGORIES, payload: subcategories };
};

const setProducts = (products: ProductModel[]) => {
  return { type: types.SET_PRODUCTS, payload: products };
};

const setFilters = (filters: any[]) => {
  return { type: types.SET_CATEGORIES_FILTERS, payload: filters };
};

const setBrands = (brands: any[]) => {
  return { type: types.SET_CATEGORIES_BRANDS, payload: brands };
};

const setAttributes = (attributes: any[]) => {
  return { type: types.SET_CATEGORIES_ATTRIBUTES, payload: attributes };
};

const setFilteredProducts = (filteredProducts: ProductModel[]) => {
  return { type: types.SET_FILTERED_PRODUCTS, payload: filteredProducts };
};

const setActiveSubcategory = (subcategory: { id: string; name: string }) => {
  return { type: types.SET_ACTIVE_SUBCATEGORY, payload: subcategory };
};

const setActiveFilters = (filters: SubcategoryFilter[]) => {
  return { type: types.SET_ACTIVE_FILTERS, payload: filters };
};

const setActiveBrands = (brands: SubcategoryBrandFilter[]) => {
  return { type: types.SET_ACTIVE_BRANDS, payload: brands };
};

const setActiveAttributes = (attributes: SubcategoryAttributeFilter[]) => {
  return { type: types.SET_ACTIVE_ATTRIBUTES, payload: attributes };
};

/* Error handling */

const categoriesError = (hasFailed: boolean, error: any) => {
  return {
    type: types.CATEGORIES_ERROR,
    payload: {
      hasFailed,
      error
    }
  };
};

const clearError = () => {
  return {
    type: types.CATEGORIES_ERROR,
    payload: {
      hasFailed: false,
      error: ''
    }
  };
};

export default {
  setCategories,
  setSubcategories,
  setProducts,
  setFilteredProducts,
  getCategories,
  getProductsByCategory,
  getProductsByCategoryPending,
  getSubcategories,
  getSubcategoriesPending,
  getCategoryPending,
  getFiltersBySubcategory,
  getFiltersBySubcategoryPending,
  getBrandsBySubcategory,
  getAttributesBySubcategory,
  getFilteredProducts,
  getFilteredProductsPending,
  setFilters,
  setBrands,
  setAttributes,
  setActiveSubcategory,
  setActiveFilters,
  setActiveBrands,
  setActiveAttributes,
  categoriesError,
  clearError
};
